// SaveOrderTable.jsx
import React, { useMemo } from "react";
import CommonDataTable from "../../components/CommonDataTable";
import { IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { itemFields } from "./formFields/salesOrderSchema.js";

/**
 * SaveOrderTable
 * Props:
 *  - data: array
 *  - onEdit: fn(rowOrLineNumber)  (optional)
 *  - onDelete: fn(lineNumberOrId) (optional)
 *  - onView: fn(row) (optional)
 *  - readOnly: boolean (optional)
 */
function RowActionMenu({ params, onEditRow, onDeleteRow, onViewRow }) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const handleClick = (e) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const row = params?.row ?? {};

    if (!onEditRow && !onDeleteRow && !onViewRow) return null;

    return (
        <>
            <IconButton size="small" onClick={handleClick}>
                <MoreVertIcon />
            </IconButton>
            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                {onEditRow && (
                    <MenuItem
                        onClick={() => {
                            onEditRow?.(row);
                            handleClose();
                        }}
                    >
                        <EditIcon fontSize="small" style={{ marginRight: 8 }} /> Edit
                    </MenuItem>
                )}
                {onDeleteRow && (
                    <MenuItem
                        onClick={() => {
                            const identifierForDelete = row.lineNumber ?? row.id;
                            onDeleteRow?.(identifierForDelete);
                            handleClose();
                        }}
                    >
                        <DeleteIcon fontSize="small" style={{ marginRight: 8 }} /> Remove
                    </MenuItem>
                )}
            </Menu>
        </>
    );
}

const actionsCol = (onEditRow, onDeleteRow, onViewRow) => ({
    field: "actions",
    headerName: "Actions",
    width: 120,
    sortable: false,
    filterable: false,
    align: "left",
    renderCell: (params) => (
        <RowActionMenu
            params={params}
            onEditRow={onEditRow}
            onDeleteRow={onDeleteRow}
            onViewRow={onViewRow}
        />
    ),
});

export default function SaveOrderTable({ data = [], onEdit, onDelete, onView, readOnly = false }) {
    // Build columns from schema
    const itemColsFromSchema = useMemo(() => {
        const mapped = (itemFields || [])
            .filter((f) => {
                const label = (f.label || "").toLowerCase();
                const name = (f.name || "").toLowerCase();
                if (label === "item (sku fallback)") return false;
                if (label.includes("sku fallback")) return false;
                if (name === "item") return false;
                if (name === "skufallback" || name === "sku_fallback") return false;
                return true;
            })
            .map((f) => {
                const col = {
                    field: f.name,
                    headerName: f.label || f.name,
                    width: f.width || (f.name === "itemPerPrice" ? 130 : 140),
                };
                if (f.name === "itemPerPrice") {
                    col.renderCell = (params) => {
                        const n = Number(params.value);
                        return Number.isFinite(n) ? n.toFixed(2) : (params.value ?? "");
                    };
                }
                return col;
            });

        return mapped;
    }, []);

    // Ensure qcRequired column exists and renders Yes/No
    const withQcRequiredCol = useMemo(() => {
        const hasQc = itemColsFromSchema.some((c) => c.field === "qcRequired");
        if (hasQc) {
            // augment existing to render Yes/No
            return itemColsFromSchema.map((c) =>
                c.field === "qcRequired"
                    ? {
                        ...c,
                        renderCell: (params) =>
                            params.value === true || params.value === "Yes"
                                ? "Yes"
                                : params.value === false || params.value === "No"
                                    ? "No"
                                    : "",
                    }
                    : c
            );
        }
        // add qcRequired if missing
        return [
            ...itemColsFromSchema,
            {
                field: "qcRequired",
                headerName: "QC Required",
                width: 140,
                renderCell: (params) =>
                    params.value === true || params.value === "Yes"
                        ? "Yes"
                        : params.value === false || params.value === "No"
                            ? "No"
                            : "",
            },
        ];
    }, [itemColsFromSchema]);

    const ALL_ITEM_COLUMNS = useMemo(() => {
        return [actionsCol(onEdit, onDelete, onView), ...withQcRequiredCol];
    }, [onEdit, onDelete, onView, withQcRequiredCol]);

    const initialVisible = useMemo(() => {
        return ALL_ITEM_COLUMNS.map((c) => c.field);
    }, [ALL_ITEM_COLUMNS]);

    const initialRows = useMemo(
        () =>
            (data || []).map((row, idx) => {
                console.log("...................", row)
                const normalized = {
                    // spread FIRST so our overrides stick
                    ...row,
                    id: row.id ?? row.lineNumber ?? `row-${idx + 1}`,
                    lineNumber:
                        row.lineNumber ??
                        row._raw?.detail?.lineNumber ??
                        row.id ??
                        `row-${idx + 1}`,
                    skuId: row.skuId ?? row.item ?? row.sku ?? row._raw?.detail?.skuId ?? "",
                    description:
                        row.description ?? row.desc ?? row._raw?.detail?.description ?? "",
                    orderQty:
                        row.orderQty ??
                        row.orderedQty ??
                        row.qty ??
                        row._raw?.detail?.orderedQty ??
                        0,
                        itemPOTolerance:
                        row.itemPOTolerance ??
                        row.tolerance ??0,
                    pack: row.pack ?? row._raw?.detail?.pack ?? "STD",
                    uom: row.uom ?? row._raw?.detail?.uom ?? "EA",
                    itemweight: row.itemweight ?? row._raw?.detail?.itemWeight ?? "",
                    tolerance: row.tolerance ?? row.itemPOTolerance ?? 0,
                    // FINAL override: normalize qcRequired to Yes/No (or empty)
                    qcRequired:
                        row.qcRequired === true
                            ? "Yes"
                            : row.qcRequired === false
                                ? "No"
                                : row.qcRequired === "Yes" || row.qcRequired === "No"
                                    ? row.qcRequired
                                    : "",
                };


                // prevent fallback fields from appearing
                delete normalized.item;
                delete normalized.skuFallback;
                delete normalized.sku_fallback;
                

                return normalized;
            }),
        [data]
    );

    return (
        <div className="mt-4 mb-8">
            <CommonDataTable
                id="so-items-actions-first"
                allPossibleColumns={ALL_ITEM_COLUMNS}
                initialVisible={initialVisible}
                initialRows={initialRows}
                height={500}
                pageSize={5}
                rowsPerPageOptions={[5, 10, 20]}
                forItems
                forItemsexport={true}
                checkboxSelection
            />
        </div>
    );
}
